import React, { useState, useEffect } from 'react';
import { app } from '../firebase'; // Importez votre configuration Firebase
import { getFirestore, collection, doc, updateDoc, onSnapshot } from 'firebase/firestore'; 
import Loader from '../components/Loader';
import { isCoownership, useUser } from '../contexts/UserContext';
import useDeleteUserFromCoownership from '../coownership/hooks/useDeleteUserFromCoownership';
import DeleteModal from '../components/modals/DeleteModal';
import PrimaryButton from '../components/buttons/PrimaryButton';
import { downloadCsvFile } from '../librairies/CsvLib';

const db = getFirestore(app);

const ROLE_LABELS = {
  owner: "Copropriétaire",
  rentalManager: "Gestionnaire locatif",
  rentalManagerEmail: "Gestionnaire locatif",
};

const mapOwner = (owner) => ({
  id: owner.id,
  role: "owner",
  firstname: owner.data().firstname,
  lastname: owner.data().lastname,
  email: owner.data().email,
  phone: owner.data().phone,
  company: "",
  coownershipComitee: owner.data().coownershipComitee,
  addressDetail: owner.data().addressDetail,
});

const mapRentalManager = (manager) => ({
  id: manager.id,
  role: "rentalManager",
  firstname: manager.data().firstname,
  lastname: manager.data().lastname,
  email: manager.data().email,
  phone: manager.data().phone,
  company: manager.data().company || "",
  coownershipComitee: false,
  addressDetail: "",
});

const mapRentalManagerEmail = (email, index) => ({
  id: `rental-manager-email-${index}`,
  role: "rentalManagerEmail",
  firstname: "",
  lastname: "",
  email,
  phone: "",
  company: "",
  coownershipComitee: false,
  addressDetail: "",
  sourceIndex: index,
});

const ListOwner = () => {

  const [owners, setOwners] = useState([]);
  const [rentalManagers, setRentalManagers] = useState([]);
  const [rentalManagerEmails, setRentalManagerEmails] = useState([]);
  const [storedRentalManagerEmails, setStoredRentalManagerEmails] = useState(["", "", ""]);
  const [ownersLoaded, setOwnersLoaded] = useState(false);
  const [rentalManagersLoaded, setRentalManagersLoaded] = useState(false);
  const [rentalManagerEmailsLoaded, setRentalManagerEmailsLoaded] = useState(false);
  const { deleteOwnerFromCoownership, deleteRentalManagerFromCoownership } = useDeleteUserFromCoownership(); 
  const [showDeleteModal, setShowDeleteModal] = useState(false); 
  const [elementToDelete, setElementToDelete] = useState(null); 

  //contexte de l'utilisateur connecté
  const user = useUser();
  const canRemoveRentalManager = isCoownership(user.user);
  const coownershipPath = user.user.userData.lastCoownershipLoaded.path;
  const members = [...owners, ...rentalManagers, ...rentalManagerEmails];

  useEffect(() => {
    const collectionRef = collection(db, coownershipPath+"/owner");

    const unsubscribe = onSnapshot(collectionRef, (snapshot) => {
      setOwners(snapshot.docs.map(mapOwner));
      setOwnersLoaded(true);
    });

    return () => {
      console.log("unsubscribe");
      unsubscribe();
    }
  }, [coownershipPath]);

  useEffect(() => {
    const collectionRef = collection(db, coownershipPath+"/usersRentalManager");

    const unsubscribe = onSnapshot(collectionRef, (snapshot) => {
      setRentalManagers(snapshot.docs.map(mapRentalManager));
      setRentalManagersLoaded(true);
    });

    return () => {
      unsubscribe();
    }
  }, [coownershipPath]);

  useEffect(() => {
    const docRef = doc(db, coownershipPath);

    const unsubscribe = onSnapshot(docRef, (snapshot) => {
      const stored = snapshot.exists() && Array.isArray(snapshot.data().rentalManagerEmails)
        ? snapshot.data().rentalManagerEmails
        : [];
      const padded = [0, 1, 2].map((index) => String(stored[index] ?? "").trim());
      setStoredRentalManagerEmails(padded);
      setRentalManagerEmails(
        padded
          .map((email, index) => ({ email, index }))
          .filter((item) => item.email !== "")
          .map((item) => mapRentalManagerEmail(item.email, item.index))
      );
      setRentalManagerEmailsLoaded(true);
    });

    return () => {
      unsubscribe();
    }
  }, [coownershipPath]);


  useEffect(() => {
    if(elementToDelete != null){
      setShowDeleteModal(true); 
    }
  }, [elementToDelete]);

  useEffect(() => {
    if(showDeleteModal === false){
      setElementToDelete(null);
    }
  }, [showDeleteModal]);

  /**
   * Retire un copropriétaire, le rattachement d'un compte gestionnaire locatif,
   * ou une adresse de rentalManagerEmails. Le compte gestionnaire locatif est conservé.
   */
  const deleteMember = async () => {
    if(elementToDelete === null){
      return;
    }

    if(elementToDelete.role === "rentalManager"){
      if(!canRemoveRentalManager){
        setShowDeleteModal(false);
        return;
      }
      await deleteRentalManagerFromCoownership(elementToDelete.id, coownershipPath);
    }else if(elementToDelete.role === "rentalManagerEmail"){
      if(!canRemoveRentalManager){
        setShowDeleteModal(false);
        return;
      }
      const nextEmails = [...storedRentalManagerEmails];
      nextEmails[elementToDelete.sourceIndex] = "";
      await updateDoc(doc(db, coownershipPath), {
        rentalManagerEmails: nextEmails,
      });
    }else{
      await deleteOwnerFromCoownership(elementToDelete.id, coownershipPath);
    }

    setShowDeleteModal(false);
  }

  /**
   * Permet de modifier les membres du conseil syndical
   * @param {*} userId 
   * @param {*} integrate 
   */
  const setCoownershipComitee = async (userId, integrate) => {
    const docRef = doc(db, coownershipPath+"/owner/"+userId);

    await updateDoc(docRef, {
      coownershipComitee: integrate
    });
  }

  const downloadCSV = () => {
    const headers = [
      "Prenom",
      "Nom",
      "Email",
      "Telephone",
      "Entreprise",
      "Numero de logement",
      "Membre conseil syndic",
      "Role",
    ];

    const rows = members.map((member) => [
      member.firstname || "",
      member.lastname || "",
      member.email || "",
      member.phone || "",
      member.company || "",
      member.role === "owner" ? (member.addressDetail || "") : "",
      member.role === "owner" ? (member.coownershipComitee ? "Oui" : "Non") : "",
      ROLE_LABELS[member.role],
    ]);

    const coownershipId = user.user.userData.lastCoownershipLoaded?.id || "copropriete";
    downloadCsvFile({
      headers,
      rows,
      filename: `membres-proprietaires-${coownershipId}.csv`,
    });
  };

  const loader = !ownersLoaded || !rentalManagersLoaded || !rentalManagerEmailsLoaded;

  return (
    <div>
      {loader ? (
        <Loader />
      ) : (
        <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
          <div className='flex gap-4 justify-end p-2'>
            <a href="#" onClick={(e) => { e.preventDefault(); downloadCSV(); }} className="justify-end flex mb-4">
              <PrimaryButton type={"button"} value={"Exporter en csv"} loading={false} />
            </a>
          </div>
          <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
            <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
              <tr>
                <th scope="col" className="px-6 py-3">
                    Prénom
                </th>
                <th scope="col" className="px-6 py-3">
                    Nom
                </th>
                <th scope="col" className="px-6 py-3">
                    Rôle
                </th>
                <th scope="col" className="px-6 py-3">
                    Email
                </th>
                <th scope="col" className="px-6 py-3">
                    Téléphone
                </th>
                <th scope="col" className="px-6 py-3">
                    Entreprise
                </th>
                <th scope="col" className="px-6 py-3">
                    Appartement
                </th>
                <th scope="col" className="px-6 py-3">
                    Membre Conseil Syndic (CS)
                </th>
                <th scope="col" className="px-6 py-3">
                    Action
                </th>
              </tr>
            </thead>
            <tbody>
              {members.length > 0 ? 
                members.map((member, key) => {
                  const isOwner = member.role === "owner";

                  return (
                    <tr key={`${member.role}-${member.id}`} className={key % 2 === 0 ? "bg-white border-b dark:bg-gray-900 dark:border-gray-700" : "border-b bg-gray-50 dark:bg-gray-800 dark:border-gray-700"}>
                      <th scope="row" className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white">
                        {member.firstname}
                      </th>
                      <td className="px-6 py-4">
                        {member.lastname}
                      </td>
                      <td className="px-6 py-4">
                        {ROLE_LABELS[member.role]}
                      </td>
                      <td className="px-6 py-4">
                        {member.email}
                      </td>
                      <td className="px-6 py-4">
                        {member.phone}
                      </td>
                      <td className="px-6 py-4">
                        {member.company || "—"}
                      </td>
                      <td className="px-6 py-4">
                        {isOwner ? member.addressDetail : "—"}
                      </td>
                      <td className="px-6 py-4">
                        {isOwner ? (member.coownershipComitee ? "Oui" : "Non") : "—"}
                      </td>
                      <td className="px-6 py-4 flex gap-4">
                        {isOwner ? (
                          <>
                            <a href="#" onClick={() => setCoownershipComitee(member.id, !member.coownershipComitee)} className="font-medium text-blue-600 dark:text-blue-500 hover:underline">{member.coownershipComitee ? "Retirer du CS" : "Intégrer au CS"}</a>
                            <a href={"mailto:support@trustymo.com?subject=Demande de blocage du compte "+member.email} className="font-medium text-red-600 dark:text-red-500 hover:underline">Bloquer</a>
                            <a href="#" onClick={(event) => { event.preventDefault(); setElementToDelete({ id: member.id, role: member.role }); }} className="font-medium text-red-600 dark:text-red-500 hover:underline">Supprimer</a>
                          </>
                        ) : (member.role === "rentalManager" || member.role === "rentalManagerEmail") && canRemoveRentalManager ? (
                          <a href="#" onClick={(event) => { event.preventDefault(); setElementToDelete({ id: member.id, role: member.role, sourceIndex: member.sourceIndex }); }} className="font-medium text-red-600 dark:text-red-500 hover:underline">Supprimer</a>
                        ) : "—"}
                      </td>
                    </tr>
                  )
                })
              : null }
            </tbody>
          </table>
        </div>
      )}
      
      {showDeleteModal ? (
        <DeleteModal closeAction={() => setShowDeleteModal(false)} confirmAction={() => deleteMember()}/>
      ) : null }
    </div>
  );
};

export default ListOwner;
